import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { products } from '../data/mockData';
import Accordion from '../components/ui/Accordion';
import { ProductDetailSkeleton } from '../components/ui/Skeleton';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';

const frames = [
  { id: 'none', name: 'Print Only', price: 0, class: 'border-0 p-0 shadow-lg', thumb: 'bg-white border border-gray-200' },
  { id: 'black', name: 'Gallery Black', price: 75, class: 'border-[16px] md:border-[20px] border-[#1a1a1a] shadow-2xl', thumb: 'bg-[#1a1a1a]' },
  { id: 'white', name: 'Gallery White', price: 75, class: 'border-[16px] md:border-[20px] border-white shadow-2xl', thumb: 'bg-white border border-gray-200 shadow-sm' },
  { id: 'oak', name: 'Natural Oak', price: 85, class: 'border-[16px] md:border-[20px] border-[#c19a6b] shadow-2xl', thumb: 'bg-[#c19a6b]' },
];

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState(frames[0]);
  const [isAdding, setIsAdding] = useState(false);
  
  useEffect(() => {
    window.scrollTo(0, 0);
    setIsLoading(true);
    setError(false);
    
    // Simulate API fetch
    const timer = setTimeout(() => {
      const foundProduct = products.find(p => p.id === id);
      if (foundProduct) {
        setProduct(foundProduct);
      } else {
        setError(true);
      }
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, [id]);

  const handleAddToCart = () => {
    setIsAdding(true);
    addToCart(product, selectedFrame);
    setTimeout(() => setIsAdding(false), 1500);
  };

  if (isLoading) return <ProductDetailSkeleton />;

  if (error || !product) {
    return (
      <div className="container mx-auto px-6 py-32 text-center min-h-[60vh] flex flex-col items-center justify-center">
        <SafeIcon icon={FiIcons.FiAlertCircle} className="text-5xl text-gray-300 mb-6" />
        <h1 className="text-3xl font-serif mb-4">Artwork Not Found</h1>
        <p className="text-gray-500 mb-8 max-w-md">The piece you are looking for may have been archived or removed from the portfolio.</p>
        <Link 
          to="/shop" 
          className="bg-charcoal text-white px-8 py-4 uppercase tracking-widest text-[11px] font-bold hover:bg-ultramarine transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine focus-visible:ring-offset-2"
        >
          Return to Portfolio
        </Link>
      </div>
    );
  }

  const totalPrice = product.price + selectedFrame.price;

  return (
    <div className="container mx-auto px-6 md:px-12 py-12 animate-fade-in">
      <nav aria-label="Breadcrumb" className="text-[10px] uppercase tracking-[0.3em] font-bold text-gray-500 mb-8 md:mb-12 flex items-center gap-3 overflow-x-auto whitespace-nowrap hide-scrollbar pb-2">
        <Link to="/" className="hover:text-ultramarine transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine rounded-sm px-1">Home</Link>
        <span className="text-gray-300" aria-hidden="true">/</span>
        <Link to="/shop" className="hover:text-ultramarine transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine rounded-sm px-1">Collection</Link>
        <span className="text-gray-300" aria-hidden="true">/</span>
        <span className="text-charcoal px-1" aria-current="page">{product.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
        {/* Gallery View */}
        <div className="lg:col-span-7">
          <div className="bg-[#f2f2f2] aspect-square md:aspect-[4/5] flex items-center justify-center p-6 md:p-20 sticky top-32 overflow-hidden rounded-sm">
            <motion.div 
              key={selectedFrame.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className={`bg-white transition-all duration-500 overflow-hidden ${selectedFrame.class}`}
            >
              <img 
                src={product.image} 
                alt={`${product.title} presented in ${selectedFrame.name}`} 
                className="w-full h-full object-contain border-[8px] md:border-[30px] border-white" 
              />
            </motion.div>
            
            <div className="absolute bottom-6 right-6 text-[10px] uppercase tracking-widest text-gray-500 font-bold hidden md:block" aria-hidden="true">
              Scale: {product.dimensions} Reference
            </div>
          </div>
        </div>

        {/* Purchase Interface */}
        <div className="lg:col-span-5 flex flex-col pt-4">
          <div className="mb-10">
            <span className="text-xs tracking-[0.2em] uppercase text-gray-500 font-bold mb-3 block">Artist Series: {product.series}</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif mb-4 leading-tight text-charcoal">{product.title}</h1>
            <p className="text-xl text-gray-600 font-serif italic mb-8">by {product.artist}</p>
            <div className="flex flex-wrap items-baseline gap-4">
              <span className="text-3xl text-ultramarine font-semibold">${totalPrice}</span>
              <span className="text-xs text-gray-500 uppercase tracking-widest">Inc. VAT & Shipping</span>
            </div>
          </div>

          <div className="mb-12">
            <div className="flex justify-between items-baseline mb-6">
              <h2 className="uppercase tracking-[0.2em] text-[11px] font-bold text-charcoal" id="frame-selector-label">1. Select Framing</h2>
              <Link to="/info" className="text-[10px] text-ultramarine font-bold underline uppercase tracking-widest focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine rounded-sm px-1">Size Guide</Link>
            </div>
            
            <div 
              className="grid grid-cols-2 gap-4" 
              role="radiogroup" 
              aria-labelledby="frame-selector-label"
            >
              {frames.map(frame => (
                <button
                  key={frame.id}
                  role="radio"
                  aria-checked={selectedFrame.id === frame.id}
                  onClick={() => setSelectedFrame(frame)}
                  className={`flex flex-col items-center p-5 border transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine focus-visible:ring-offset-2 min-h-[44px] rounded-sm ${
                    selectedFrame.id === frame.id 
                      ? 'border-ultramarine bg-ultramarine/5 ring-1 ring-ultramarine' 
                      : 'border-gray-200 hover:border-gray-400 bg-white'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full mb-3 ${frame.thumb}`} aria-hidden="true" />
                  <span className="text-[11px] font-bold uppercase tracking-widest mb-1 text-charcoal text-center">{frame.name}</span>
                  <span className="text-[10px] text-gray-600">+{frame.price > 0 ? `$${frame.price}` : 'Free'}</span>
                </button>
              ))}
            </div>
          </div>

          <button 
            onClick={handleAddToCart}
            disabled={isAdding}
            aria-live="polite"
            className={`w-full py-5 md:py-6 uppercase tracking-[0.3em] text-[11px] font-bold transition-all duration-500 relative overflow-hidden min-h-[56px] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ultramarine focus-visible:ring-offset-2 rounded-sm ${
              isAdding ? 'bg-green-600 text-white' : 'bg-charcoal text-white hover:bg-ultramarine'
            }`}
          >
            <AnimatePresence mode="wait">
              {isAdding ? (
                <motion.span 
                  key="added"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -20, opacity: 0 }}
                  className="flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiIcons.FiCheck} className="text-lg" /> Added to Collection
                </motion.span>
              ) : (
                <motion.span
                  key="add"
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -20, opacity: 0 }}
                >
                  Add to Collection — ${totalPrice}
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <div className="mt-12 space-y-0 divide-y divide-gray-200 border-t border-gray-200">
            <Accordion 
              title="Curator's Notes" 
              content={
                <div className="py-2">
                  <p className="mb-6 text-base leading-relaxed text-gray-600">{product.notes}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-4 text-[11px] uppercase tracking-widest">
                    <div><span className="text-gray-500 block mb-1">Medium</span> <span className="font-semibold text-charcoal">{product.medium}</span></div>
                    <div><span className="text-gray-500 block mb-1">Sheet Size</span> <span className="font-semibold text-charcoal">{product.dimensions}</span></div>
                    <div><span className="text-gray-500 block mb-1">Edition</span> <span className="font-semibold text-charcoal">Limited Release</span></div>
                    <div><span className="text-gray-500 block mb-1">Matting</span> <span className="font-semibold text-charcoal">2" Acid-Free White</span></div>
                  </div>
                </div>
              }
            />
            <Accordion 
              title="Delivery & Installation" 
              content="All prints are meticulously produced to order. Framed works include professional-grade hanging wire and wall bumpers. Shipping is carbon-neutral and insured for the full value of the work. Estimated production lead time: 5-8 business days."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;